/**
 * This is a controller to freely use form state in side bars and footers etc
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import noop from 'lodash/fp/noop';
import type { SidebarId } from './JiraFieldCategoryType.types.tsx';
import type {
	FormStateContext,
	GlobalState,
	FormState,
	FormValues,
} from './FormStateContext.types.tsx';

const INITIAL_STATE: GlobalState = {};

const INITIAL_CONTEXT_STATE: FormStateContext = {
	state: INITIAL_STATE,
	setSidebarState: () => noop,
	resetSidebar: () => noop,
};

const FormStateContext = createContext<FormStateContext>(INITIAL_CONTEXT_STATE);

export const useFormState = () => useContext(FormStateContext);

export const SidebarFormsProvider = ({ children }: { children: React.ReactNode }) => {
	const [state, setState] = useState<GlobalState>(INITIAL_STATE);

	const resetSidebar = useCallback((sidebarId: SidebarId | undefined) => {
		setState((current) => {
			if (!sidebarId) return current;
			const nextState = { ...current };
			delete nextState[sidebarId];
			return nextState;
		});
	}, []);

	const resetAll = useCallback(() => {
		setState(INITIAL_STATE);
	}, []);

	useEffect(() => {
		return resetAll;
	}, [resetAll]);

	const setSidebarState = useCallback(
		(sidebarId: SidebarId | undefined) => (nextState: Partial<FormState<FormValues>>) => {
			if (!sidebarId) return;
			setState((currentState) => {
				const currentSidebarState = currentState[sidebarId];
				const nextSidebarState = { ...currentSidebarState, ...nextState };
				return { ...currentState, [sidebarId]: nextSidebarState };
			});
		},
		[],
	);

	const contextValue = useMemo(() => {
		return {
			state,
			setSidebarState,
			resetSidebar,
		};
	}, [setSidebarState, state, resetSidebar]);

	return <FormStateContext.Provider value={contextValue}>{children}</FormStateContext.Provider>;
};
